// React i hooks
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

// Tipovi i konstante
import {
  ResidualValueSearchPageCmp,
  ResidualValueSearchPageProps,
} from "./types";
import {
  ResidualValueRow,
  ResidualValueSearchFormValues,
} from "services/api/calculations/residualvalue/types";
import { DataWithPagination } from "services/api";
import { initialDataWithPagination } from "services/api/consts";
import { Pagination } from "services/search";
import {
  CALCULATION_RESIDUAL_VALUE_CREATE_PAGE_TITLE,
  CALCULATION_RESIDUAL_VALUE_SEARCH_PAGE_TITLE,
} from "ui/components/PageTitle/consts";

// API servisi
import { getResidualValuesAPI } from "services/api/calculations/residualvalue/api";

// UI komponente
import { Breadcrumb } from "ui/components/Breadcrumb";
import { PageHeader } from "ui/components/PageHeader";
import { PageTitle } from "ui/components/PageTitle";
import { Modal } from "ui/components/Modal";
import { useSelectedItemsChanges } from "ui/components/Table/hooks";
import { NoResults } from "ui/components/NoResults";
import ModalBody from "ui/components/Modal/ModalBody";
import ModalHeader from "ui/components/Modal/ModalHeader";
import TabMenu from "../TabMenu/TabMenu";
import ResidualValueEditCard from "../ResidualValueEditCard/ResidualValueEditCard";

// Lokalne komponente
import ResidualValueSearchFilters from "./components/ResidualValueSearchFilters/ResidualValueSearchFilters";
import ResidualValueSearchResults from "./components/ResidualValueSearchResults/ResidualValueSearchResults";

// Helperi
import { transformRoute, useTranslate } from "helpers";

// URL i navigacija
import { Routes } from "ui/modules/calculation/navigation";

const ResidualValueSearchPage: ResidualValueSearchPageCmp = (
  props: ResidualValueSearchPageProps
) => {
  const { t } = useTranslate();
  const [searchResults, setSearchResults] = useState<
    DataWithPagination<ResidualValueRow>
  >(initialDataWithPagination);

  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const [residualValueId, setResidualValueId] = useState<
    string | null
  >(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showAddNewModal, setShowAddNewModal] = useState<boolean>(false);

  const navigate = useNavigate();

  useEffect(() => {
    fetchSearchResult(searchResults.pagination);
  }, []);

  const fetchSearchResult = async (
    pagination: Pagination,
    name?: string | null
  ) => {
    setIsLoading(true);
    try {
      const results = await getResidualValuesAPI(pagination, name);

      setSearchResults(results);
    } catch {
      setSearchResults(initialDataWithPagination);
      setIsLoading(false);
      return;
    }
    setIsLoading(false);
  };

  const handleRowClick = useCallback(async (id: string) => {
    navigate(
      transformRoute(Routes.CalculationResidualValueDetailPageRouter, {
        residualValueId: id,
      })
    );
  }, []);

  const handleAddNew = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      setResidualValueId("");
      setShowAddNewModal(true);
    },
    [setResidualValueId, setShowAddNewModal]
  );

  const handleSearchChange = useCallback(
    (searchFormValues: ResidualValueSearchFormValues) => {
      fetchSearchResult(searchResults.pagination, searchFormValues.search);
    },
    []
  );

  const handleEditSuccess = useCallback(() => {
    fetchSearchResult(searchResults.pagination);
    setResidualValueId(null);
    setShowAddNewModal(false);
  }, [setResidualValueId, setShowAddNewModal]);

  const handleSelectedChange = useSelectedItemsChanges(
    selectedRows,
    setSelectedRows
  );

  const handlePaginationChange = (newPagination: Pagination) => {
    fetchSearchResult(newPagination);
  };

  return (
    <>
      <div className="">
        <div className="bg-white rounded-t-md">
          <PageHeader>
            <PageTitle
              title={CALCULATION_RESIDUAL_VALUE_SEARCH_PAGE_TITLE.text}
              icon={CALCULATION_RESIDUAL_VALUE_SEARCH_PAGE_TITLE.icon}
              iconColor={CALCULATION_RESIDUAL_VALUE_SEARCH_PAGE_TITLE.iconColor}
            />
            <Breadcrumb />
          </PageHeader>
          <TabMenu />
        </div>
        <div className="px-0 pt-2 mx-[-1px] xl:px-6">
          <ResidualValueSearchFilters
            onSearchChange={handleSearchChange}
            onAddNew={handleAddNew}
          />
          {!isLoading && searchResults.pagination.totalPages === 0 ? (
            <NoResults
              message={t(
                "admin.calculation.setting.residualvalue.search.noresults"
              )}
            />
          ) : (
            <ResidualValueSearchResults
              searchResults={searchResults}
              selectedRows={selectedRows}
              isLoading={isLoading}
              onRowClick={handleRowClick}
              onPaginationChange={handlePaginationChange}
              onSelectedChange={handleSelectedChange}
            />
          )}
        </div>
      </div>
      {showAddNewModal && (
        <Modal
          show={showAddNewModal}
          onHide={() => setShowAddNewModal(false)}
          size="full"
        >
          <ModalHeader
            title={CALCULATION_RESIDUAL_VALUE_CREATE_PAGE_TITLE.text}
            onHide={() => setShowAddNewModal(false)}
          />
          <ModalBody>
            <ResidualValueEditCard
              residualValueId={residualValueId}
              onSuccess={handleEditSuccess}
            />
          </ModalBody>
        </Modal>
      )}
    </>
  );
};

ResidualValueSearchPage.route = Routes.CalculationResidualValueSearchPageRouter;

export default ResidualValueSearchPage;
